import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";

import { Roles } from "@/shared/utils/constants";
import { useSurveyPermissions } from "@/features/survey/hooks/useSurveyPermissions";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import { useGetSurveyByIdQuery } from "@/redux/service";
import SurveyManagement from "@/features/survey/management";
import SurveySummary from "@/features/survey/summary";
import { isSurveyAdminResponse } from "@/redux/survey/typeGuards";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { useOrganizationId } from "@/shared/hooks/useOrganizationId";

const SurveyDetails = () => {
  const { surveyId } = useParams();
  const [searchParams] = useSearchParams();

  const sampleOrganizationId = searchParams.get("sampleOrganizationId");
  const currentOrganizationId = useOrganizationId();
  const { data: survey, isError } = useGetSurveyByIdQuery(
    surveyId
      ? {
          surveyId,
          filters: {
            organization: sampleOrganizationId || currentOrganizationId,
          },
        }
      : skipToken,
  );
  const currentUser = useSelector(selectCurrentUser);
  const {
    canView,
    canEdit,
    isLoading: isLoadingPermissions,
  } = useSurveyPermissions(survey, currentUser);

  if (!survey || isLoadingPermissions || !currentUser) {
    return (
      <div className="flex justify-center full-width">
        <BaseSpinner />
      </div>
    );
  }

  if (isError || !canView) {
    return <Navigate to="/" />;
  }

  if (currentUser.role === Roles.ADMIN.value) {
    if (!isSurveyAdminResponse(survey)) {
      return <Navigate to="/" />;
    }
    return <SurveyManagement survey={survey} />;
  }

  if (canEdit || sampleOrganizationId) {
    return <SurveySummary survey={survey} />;
  }

  return <Navigate to={`/reports/${survey.id}/overview`} replace />;
};

export default SurveyDetails;
